/* eslint-disable max-lines-per-function */
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { LocalTrendingPetitionsItem } from '#src/app/pages/local/details/pageContext';
import {
	useLocalPageData,
	useLocalPagePaginatedRecentPetitionsData,
	useLocalPagePaginatedTrendingPetitionsData,
} from '#src/app/pages/local/details/pageContext';

export type Section = {
	id: string;
	displayName: string;
	petitions: readonly LocalTrendingPetitionsItem[];
	hasNextPage: boolean;
	nextPageLoading: boolean;
	loadMore: () => void;
};

type Result = ModelHookResult<{
	totalCountLocalized: string;
	displayName: string;
	sections: Section[];
	refreshLoading: boolean;
	actions: {
		trackTrending: () => void;
		trackMostRecent: () => void;
	};
}>;

export function useFilterablePetitionsSection(): Result {
	const {
		data: {
			items: recentPetitions,
			hasNextPage: recentHasNextPage,
			nextPageLoading: recentNextPageLoading,
			refreshLoading: recentRefreshLoading,
		},
		actions: { loadMore: recentLoadMore },
	} = useLocalPagePaginatedRecentPetitionsData();

	const {
		data: {
			items: trendingPetitions,
			hasNextPage: trendingHasNextPage,
			totalCountLocalized,
			nextPageLoading: trendingNextPageLoading,
			refreshLoading: trendingRefreshLoading,
		},
		actions: { loadMore: trendingLoadMore },
	} = useLocalPagePaginatedTrendingPetitionsData();

	const {
		data: { displayName },
	} = useLocalPageData();

	const { translate } = useI18n();
	const trackEvent = useTracking();

	return {
		data: {
			totalCountLocalized,
			displayName,
			refreshLoading: recentRefreshLoading || trendingRefreshLoading,
			sections: [
				{
					id: 'trending',
					displayName: translate('corgi.local.petition_section.trending'),
					petitions: trendingPetitions,
					hasNextPage: trendingHasNextPage,
					nextPageLoading: trendingNextPageLoading,
					loadMore: () => {
						trendingLoadMore();
						void trackEvent('local_page_load_more_petitions_click');
					},
				},
				{
					id: 'most-recent',
					displayName: translate('corgi.local.petition_section.most_recent'),
					petitions: recentPetitions,
					hasNextPage: recentHasNextPage,
					nextPageLoading: recentNextPageLoading,
					loadMore: () => {
						recentLoadMore();
						void trackEvent('local_page_load_more_petitions_click');
					},
				},
			],

			actions: {
				trackTrending: () => {
					void trackEvent('local_sort_by_trending');
				},
				trackMostRecent: () => {
					void trackEvent('local_sort_by_most_recent');
				},
			},
		},
	};
}
